/**
 * Qualified.io Form Tracking & Experience Trigger
 *
 * Currently enabled ONLY for the "Get a Demo" form.
 * The form is identified by checking whether the closest
 * .contact-form-section ancestor contains a heading with "Get a Demo".
 */

const QUALIFIED_EXPERIENCE_ID = 'experience-1785892513674';

function callQualified(...args) {
    if (typeof window.qualified === 'function') {
        window.qualified(...args);
    } else if (window.qualified && typeof window.qualified.push === 'function') {
        window.qualified.push(args);
    } else {
        window.qualified = window.qualified || [];
        window.qualified.push(args);
    }
}

function isLitmosDomain(val) {
    if (!val || typeof val !== 'string') {
        return false;
    }
    return /litmos\.com/i.test(val);
}

function deleteCookie(name) {
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
    const hostname = window.location.hostname;
    if (hostname.includes('.')) {
        const rootDomain = hostname.replace(/^www\./, '');
        document.cookie = `${name}=; path=/; domain=.${rootDomain}; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
    }
}

function getCookieValue(name) {
    const value = `; ${document.cookie}`;
    let result = '';
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        result = decodeURIComponent(parts.pop().split(';').shift());
    } else {
        const prefixedParts = value.split(`; wp_utm_${name}=`);
        if (prefixedParts.length === 2) {
            result = decodeURIComponent(prefixedParts.pop().split(';').shift());
        }
    }

    if (isLitmosDomain(result)) {
        deleteCookie(name);
        deleteCookie(`wp_utm_${name}`);
        return '';
    }

    return result;
}


/**
 * Checks whether a form element is the "Demo" form (Gravity Forms ID 6).
 * Primary check uses the form's ID attribute; falls back to checking
 * whether the closest .contact-form-section ancestor has a matching heading.
 */
function isDemoForm(formElement) {
    if (!formElement) { return false; }

    // Primary: match Gravity Forms ID 6
    if (formElement.id === 'gform_6') { return true; }

    // Fallback: heading-based check
    const section = formElement.closest('.contact-form-section');
    if (!section) { return false; }

    const heading = section.querySelector('.cfs-content .title, .cfs-content h2');
    if (!heading) { return false; }

    return (heading.textContent || heading.innerText || '').trim().toLowerCase().includes('get a demo');
}

export function extractQualifiedFormData(formElement) {
    function getFieldValue(candidates) {
        if (!formElement) { return ''; }
        for (const c of candidates) {
            if (c.selector) {
                const el = formElement.querySelector(c.selector);
                if (el && el.value && el.value.trim() !== '') {
                    return el.value.trim();
                }
            }
            if (c.label) {
                const labels = formElement.querySelectorAll('label, .gfield_label');
                for (const label of labels) {
                    const text = (label.textContent || label.innerText || '').trim().toLowerCase();
                    if (text === c.label.toLowerCase() || text.includes(c.label.toLowerCase())) {
                        const container = label.closest('.gfield') || label.parentElement;
                        if (container) {
                            const input = container.querySelector('input, select, textarea');
                            if (input && input.value && input.value.trim() !== '') {
                                return input.value.trim();
                            }
                        }
                    }
                }
            }
        }
        return '';
    }

    const email = getFieldValue([
        { selector: 'input[type="email"]' },
        { selector: 'input[name*="email" i]' },
        { selector: 'input[id*="email" i]' },
        { label: 'email' },
        { label: 'business email' },
    ]);

    const firstName = getFieldValue([
        { selector: 'input[name*="first" i]' },
        { selector: 'input[id*="first" i]' },
        { selector: '.name_first input' },
        { label: 'first name' },
        { label: 'first' },
    ]);

    const lastName = getFieldValue([
        { selector: 'input[name*="last" i]' },
        { selector: 'input[id*="last" i]' },
        { selector: '.name_last input' },
        { label: 'last name' },
        { label: 'last' },
    ]);

    let name = `${firstName} ${lastName}`.trim();
    if (!name) {
        name = getFieldValue([
            { selector: 'input[name*="name" i]' },
            { selector: 'input[id*="name" i]' },
            { label: 'name' },
            { label: 'full name' },
        ]);
    }

    const company = getFieldValue([
        { selector: 'input[name*="company" i]' },
        { selector: 'input[id*="company" i]' },
        { label: 'company' },
        { label: 'organization' },
    ]);

    const country = getFieldValue([
        { selector: 'select[name*="country" i]' },
        { selector: 'input[name*="country" i]' },
        { selector: 'select[id*="country" i]' },
        { selector: 'input[id*="country" i]' },
        { label: 'country' },
    ]);

    const phone = getFieldValue([
        { selector: 'input[type="tel"]' },
        { selector: 'input[name*="phone" i]' },
        { selector: 'input[id*="phone" i]' },
        { label: 'phone' },
        { label: 'phone number' },
        { label: 'telephone' },
    ]);

    function getParamOrCookie(paramName) {
        const val = getFieldValue([
            { selector: `input[name="${paramName}"]` },
            { selector: `input[data-cookie-label="${paramName}"]` },
            { label: paramName },
        ]);
        if (val) { return val; }

        try {
            const urlParams = new window.URLSearchParams(window.location.search);
            if (urlParams.has(paramName) && urlParams.get(paramName)) {
                return urlParams.get(paramName);
            }
        } catch (e) { // eslint-disable-line no-unused-vars
            /* ignore search param parse errors */
        }

        return getCookieValue(paramName);
    }

    let source = getParamOrCookie('source');
    if (isLitmosDomain(source)) {
        source = '';
    }

    let sourceDetail = getParamOrCookie('source_detail');
    if (isLitmosDomain(sourceDetail)) {
        sourceDetail = '';
    }

    let utm_source = getParamOrCookie('utm_source');
    if (isLitmosDomain(utm_source)) {
        utm_source = '';
    }

    const utm_medium = getParamOrCookie('utm_medium');
    const utm_campaign = getParamOrCookie('utm_campaign');
    const utm_content = getParamOrCookie('utm_content');
    const utm_term = getParamOrCookie('utm_term');
    const gclid = getParamOrCookie('gclid');
    const msclkid = getParamOrCookie('msclkid');
    const li_fat_id = getParamOrCookie('li_fat_id');
    const fbclid = getParamOrCookie('fbclid');

    return {
        email,
        name,
        company,
        country,
        phone,
        source,
        source_detail: sourceDetail,
        utm_source,
        utm_medium,
        utm_campaign,
        utm_content,
        utm_term,
        gclid,
        msclkid,
        li_fat_id,
        fbclid,
    };
}

export function initQualifiedTracking() {
    function handleFormSubmit(formElement) {
        // Only fire for the "Get a Demo" form
        if (!isDemoForm(formElement)) { return; }

        const payload = extractQualifiedFormData(formElement);
        console.log('[Qualified] saveFormData payload:', payload); // eslint-disable-line no-console, no-undef
        callQualified('saveFormData', payload);
        try {
            window.sessionStorage.setItem('qualified_pending_experience', QUALIFIED_EXPERIENCE_ID);
        } catch (e) { // eslint-disable-line no-unused-vars
            /* ignore storage errors */
        }
    }

    function fireQualifiedExperience() {
        console.log('[Qualified] showFormExperience ID:', QUALIFIED_EXPERIENCE_ID); // eslint-disable-line no-console, no-undef
        callQualified('showFormExperience', QUALIFIED_EXPERIENCE_ID);
        try {
            window.sessionStorage.removeItem('qualified_pending_experience');
        } catch (e) { // eslint-disable-line no-unused-vars
            /* ignore storage errors */
        }
    }

    document.addEventListener('submit', (e) => {
        const form = e.target;
        if (form && form.tagName === 'FORM') {
            handleFormSubmit(form);
        }
    }, true);

    if (typeof jQuery !== 'undefined') {
        // Fire saveFormData before GF's AJAX submission (native submit event doesn't fire for AJAX forms)
        jQuery(document).on('submit', '.gform_wrapper form', function() {
            handleFormSubmit(this);
        });

        jQuery(document).on('gform_confirmation_loaded', () => {
            fireQualifiedExperience();
        });
    }

    function checkPendingExperienceOnLoad() {
        try {
            if (window.sessionStorage.getItem('qualified_pending_experience')) {
                fireQualifiedExperience();
            }
        } catch (e) { // eslint-disable-line no-unused-vars
            /* ignore storage errors */
        }
    }

    // Purge any preexisting cookies that incorrectly contain litmos.com
    ['source', 'source_detail', 'utm_source', 'wp_utm_source', 'wp_utm_source_detail'].forEach((cookieName) => {
        const val = getCookieValue(cookieName);
        if (isLitmosDomain(val)) {
            deleteCookie(cookieName);
            deleteCookie(`wp_utm_${cookieName}`);
        }
    });

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', checkPendingExperienceOnLoad);
    } else {
        checkPendingExperienceOnLoad();
    }
}

export default initQualifiedTracking;
